// OrdersPage — listado operativo de órdenes con default a hoy.
//
// Flujo:
//   - Al entrar carga las órdenes de HOY (filtro server-side por planned_date).
//   - Search box rápido filtra client-side por código, cliente o ID.
//   - Botón "Historial" cambia a un rango from/to para revisar fechas pasadas.
//   - Click en una fila abre un drawer derecho con detalle: cliente, chofer,
//     boletas, monto, estado. Mantiene la lista visible — patrón Tracking.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMastersStore } from "../data/mastersStore";
import type { BadgeVariant } from "../components/ui";
import { Badge, Button, Card, Input, Select } from "../components/ui";
import { LocationMiniMap } from "../components/LocationMiniMap";
import { api, type PODHistoryEntry } from "../data/api";
import type { Order, OrderDetail, OrderStatus, Route, User } from "../data/schemaTypes";

// ---------- Helpers ----------

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateHuman(iso: string): string {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("es-PE", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: "Pendiente",
  assigned: "Asignada",
  in_route: "En ruta",
  arrived: "En sitio",
  delivered: "Entregada",
  partial: "Parcial",
  failed: "Fallida",
  cancelled: "Cancelada",
};

const STATUS_COLOR: Record<OrderStatus, string> = {
  pending: "#1976D2",
  assigned: "#1976D2",
  in_route: "#1976D2",
  arrived: "#1976D2",
  delivered: "#2E7D32",
  partial: "#FB8C00",
  failed: "#D32F2F",
  cancelled: "#D32F2F",
};

const STATUS_BADGE: Record<OrderStatus, BadgeVariant> = {
  pending: "pending",
  assigned: "in-route",
  in_route: "in-route",
  arrived: "in-route",
  delivered: "delivered",
  partial: "delayed",
  failed: "failed",
  cancelled: "failed",
};

// fmtQty — entero sin decimales; con decimales descarta los ceros al final.
function fmtQty(q: number): string {
  if (Number.isInteger(q)) return String(q);
  return q.toFixed(2).replace(/\.?0+$/, "");
}

// DivisionBadge — pinta el chip con el tipo de orden. SALUD distinto del
// resto para que el operador identifique al toque qué órdenes esperan
// repartidor (no chofer) y cuáles van por flujo planificado.
function DivisionBadge({ division }: { division?: string }) {
  const d = (division ?? "").toUpperCase();
  if (!d) {
    return <span className="text-sub text-xs">—</span>;
  }
  const colorClass =
    d === "SALUD"
      ? "bg-accent/15 text-accent border-accent/30"
      : "bg-primary/10 text-primary border-primary/30";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${colorClass}`}
    >
      {d}
    </span>
  );
}

// Driver asignado a una orden — derivado cruzando routes.stops.order_id.
// Se materializa por fecha cuando cargamos la lista para evitar N+1.
type DriverAssignment = {
  driverId: string;
  driverName: string;
  driverCode: string;
  routeId: string;
  routeCode: string;
  // null cuando el Excel no traía SECUENCIA — la UI muestra "—" en
  // lugar de "#N+1".
  sequence: number | null;
};

// ---------- Page ----------

export function OrdersPage() {
  const clients = useMastersStore((s) => s.clients);
  const clientNameById = useMemo(() => {
    const m = new Map<string, string>();
    clients.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [clients]);

  // Modo: hoy (default) o histórico (rango de fechas).
  const [mode, setMode] = useState<"day" | "range">("day");
  const [day, setDay] = useState<string>(todayISO());
  const [fromDate, setFromDate] = useState<string>(todayISO());
  const [toDate, setToDate] = useState<string>(todayISO());

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  // Filtro DIVISION: distingue el flujo CONSUMO (chofer + ruta planificada)
  // del flujo SALUD (repartidor auto-asigna al escanear). "" = ambas.
  const [divisionFilter, setDivisionFilter] = useState<string>("");

  const [orders, setOrders] = useState<Order[]>([]);
  const [drivers, setDrivers] = useState<User[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // Refetch cuando cambia el filtro de fecha (modo o rango).
  const fetchSeq = useRef(0);
  useEffect(() => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError(null);

    // Para "day" pedimos solo ese día (filtro server-side). Para "range"
    // no hay filtro de rango en /api/orders todavía, así que cargamos todo
    // y filtramos client-side. Si el dataset crece esto se debe migrar
    // a un endpoint con planned_date_from/to.
    const ordersPromise =
      mode === "day"
        ? api.listOrders({ plannedDate: day, division: divisionFilter || undefined, limit: 1000 })
        : api.listOrders({ division: divisionFilter || undefined, limit: 5000 });

    const routesPromise =
      mode === "day"
        ? api.listRoutes({ fromDate: day, toDate: day, limit: 500 })
        : api.listRoutes({ fromDate, toDate, limit: 2000 });

    Promise.all([ordersPromise, routesPromise, api.listDrivers()])
      .then(([os, rs, ds]) => {
        if (seq !== fetchSeq.current) return;
        setOrders(os);
        setRoutes(rs);
        setDrivers(ds);
      })
      .catch((e) => {
        if (seq !== fetchSeq.current) return;
        setError((e as Error).message);
      })
      .finally(() => {
        if (seq !== fetchSeq.current) return;
        setLoading(false);
      });
  }, [mode, day, fromDate, toDate, divisionFilter]);

  // Map order_id → driver. Necesita los stops de cada ruta. Como
  // /api/routes (list) no trae stops, hacemos un fetch detallado por ruta
  // en paralelo. Esto cachea por route id dentro del componente para no
  // re-fetchear al cambiar query/estado.
  const [stopsByRouteId, setStopsByRouteId] = useState<
    Map<string, { order_id: string; sequence: number | null }[]>
  >(new Map());
  useEffect(() => {
    if (routes.length === 0) {
      setStopsByRouteId(new Map());
      return;
    }
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        routes.map(async (r) => {
          try {
            const detail = await api.getRoute(r.id);
            return [
              r.id,
              (detail.stops ?? []).map((s) => ({
                order_id: s.order_id,
                sequence: s.sequence,
              })),
            ] as const;
          } catch {
            return [r.id, [] as { order_id: string; sequence: number }[]] as const;
          }
        }),
      );
      if (cancelled) return;
      setStopsByRouteId(new Map(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [routes.map((r) => r.id).join(",")]);

  // Map driver_user_id → User.
  const driverById = useMemo(() => {
    const m = new Map<string, User>();
    drivers.forEach((d) => m.set(d.id, d));
    return m;
  }, [drivers]);

  // Build order_id → DriverAssignment.
  const assignmentByOrderId = useMemo<Map<string, DriverAssignment>>(() => {
    const m = new Map<string, DriverAssignment>();
    for (const r of routes) {
      if (!r.driver_user_id) continue;
      const driver = driverById.get(r.driver_user_id);
      const stops = stopsByRouteId.get(r.id) ?? [];
      for (const s of stops) {
        m.set(s.order_id, {
          driverId: r.driver_user_id,
          driverName: driver?.full_name ?? "—",
          driverCode: driver?.code ?? "",
          routeId: r.id,
          routeCode: r.code,
          sequence: s.sequence,
        });
      }
    }
    return m;
  }, [routes, stopsByRouteId, driverById]);

  // Filtrado client-side: search + status. Prefiere el `client_name`
  // denormalizado en el wire de /api/orders; cae al store solo como
  // fallback histórico (data importada antes del cambio de schema).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (!q) return true;
      const name = (o.client_name ?? clientNameById.get(o.client_id) ?? "").toLowerCase();
      const driver = assignmentByOrderId.get(o.id);
      const driverName = (driver?.driverName ?? "").toLowerCase();
      const driverCode = (driver?.driverCode ?? "").toLowerCase();
      return (
        o.code.toLowerCase().includes(q) ||
        name.includes(q) ||
        driverName.includes(q) ||
        driverCode.includes(q)
      );
    });
  }, [orders, query, statusFilter, clientNameById, assignmentByOrderId]);

  const selectedOrder = useMemo(
    () => orders.find((o) => o.id === selectedOrderId) ?? null,
    [orders, selectedOrderId],
  );

  // Empty state cuando no hay nada que cargar para hoy y el día está limpio.
  if (!loading && orders.length === 0 && !error) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <Card padding="lg" className="max-w-md text-center">
          <h2 className="text-base font-semibold text-ink mb-2">
            Sin órdenes para {mode === "day" ? formatDateHuman(day) : "el rango"}
          </h2>
          <p className="text-sm text-sub mb-3">
            Importá la planificación de última milla o cambiá la fecha para
            ver órdenes anteriores.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Link
              to="/ultima-milla/importar"
              className="inline-block bg-primary hover:bg-primary-hover text-white text-sm font-medium rounded-md px-3 py-1.5"
            >
              Importar planificación
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setMode("range");
                setFromDate(daysAgoISO(7));
                setToDate(todayISO());
              }}
            >
              Ver últimos 7 días
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 flex min-h-0">
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="px-4 pt-4 pb-2 flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-ink">Órdenes</h1>
            <p className="text-xs text-sub mt-0.5">
              {loading
                ? "Cargando…"
                : `${filtered.length}${
                    filtered.length !== orders.length ? ` de ${orders.length}` : ""
                  } órdenes · ${
                    mode === "day"
                      ? formatDateHuman(day)
                      : `${formatDateHuman(fromDate)} → ${formatDateHuman(toDate)}`
                  }`}
            </p>
          </div>
          <Button variant="ghost" size="sm" disabled>
            ⬇ Exportar
          </Button>
        </header>

        {error && (
          <div className="mx-4 mb-2 px-3 py-2 bg-state-failed/10 border border-state-failed/30 text-xs text-state-failed rounded">
            {error}
          </div>
        )}

        <FilterBar
          mode={mode}
          day={day}
          fromDate={fromDate}
          toDate={toDate}
          query={query}
          statusFilter={statusFilter}
          divisionFilter={divisionFilter}
          onMode={setMode}
          onDay={setDay}
          onFromDate={setFromDate}
          onToDate={setToDate}
          onQuery={setQuery}
          onStatus={setStatusFilter}
          onDivision={setDivisionFilter}
        />

        <div className="flex-1 overflow-auto px-4 pb-4">
          <OrdersTable
            orders={filtered}
            clientNameById={clientNameById}
            assignmentByOrderId={assignmentByOrderId}
            selectedOrderId={selectedOrderId}
            onSelect={setSelectedOrderId}
          />
        </div>
      </div>

      {selectedOrder && (
        <OrderDetailDrawer
          orderId={selectedOrder.id}
          assignment={assignmentByOrderId.get(selectedOrder.id) ?? null}
          clientNameFallback={selectedOrder.client_name ?? clientNameById.get(selectedOrder.client_id) ?? ""}
          onClose={() => setSelectedOrderId(null)}
        />
      )}
    </div>
  );
}

// ---------- Filter bar ----------

function FilterBar({
  mode,
  day,
  fromDate,
  toDate,
  query,
  statusFilter,
  divisionFilter,
  onMode,
  onDay,
  onFromDate,
  onToDate,
  onQuery,
  onStatus,
  onDivision,
}: {
  mode: "day" | "range";
  day: string;
  fromDate: string;
  toDate: string;
  query: string;
  statusFilter: string;
  divisionFilter: string;
  onMode: (m: "day" | "range") => void;
  onDay: (d: string) => void;
  onFromDate: (d: string) => void;
  onToDate: (d: string) => void;
  onQuery: (q: string) => void;
  onStatus: (s: string) => void;
  onDivision: (d: string) => void;
}) {
  return (
    <div className="mx-4 mb-3 bg-card border border-border rounded-lg shadow-card p-3 flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[220px]">
        <Input
          label="Buscar"
          placeholder="Cliente, código, chofer…"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
      </div>

      <div className="flex items-end gap-2">
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => onMode("day")}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              mode === "day"
                ? "bg-primary text-white"
                : "bg-card text-ink hover:bg-page"
            }`}
          >
            Día
          </button>
          <button
            type="button"
            onClick={() => onMode("range")}
            className={`px-3 py-1.5 text-xs font-medium border-l border-border transition-colors ${
              mode === "range"
                ? "bg-primary text-white"
                : "bg-card text-ink hover:bg-page"
            }`}
          >
            Historial
          </button>
        </div>

        {mode === "day" ? (
          <>
            <div className="w-[160px]">
              <Input
                label="Fecha"
                type="date"
                value={day}
                onChange={(e) => onDay(e.target.value)}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDay(todayISO())}
              disabled={day === todayISO()}
              title="Volver a hoy"
            >
              Hoy
            </Button>
          </>
        ) : (
          <>
            <div className="w-[150px]">
              <Input
                label="Desde"
                type="date"
                value={fromDate}
                onChange={(e) => onFromDate(e.target.value)}
              />
            </div>
            <div className="w-[150px]">
              <Input
                label="Hasta"
                type="date"
                value={toDate}
                onChange={(e) => onToDate(e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      <div className="w-[180px]">
        <Select
          label="Estado"
          value={statusFilter}
          onChange={(e) => onStatus(e.target.value)}
        >
          <option value="">Todos</option>
          <option value="pending">Pendiente</option>
          <option value="assigned">Asignada</option>
          <option value="in_route">En ruta</option>
          <option value="delivered">Entregada</option>
          <option value="partial">Parcial</option>
          <option value="failed">Fallida</option>
          <option value="cancelled">Cancelada</option>
        </Select>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-sub">División</span>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          {([
            { v: "", label: "Todas" },
            { v: "CONSUMO", label: "Consumo" },
            { v: "SALUD", label: "Salud" },
          ] as const).map((opt) => (
            <button
              key={opt.v || "all"}
              type="button"
              onClick={() => onDivision(opt.v)}
              className={`px-3 py-1.5 text-xs font-medium border-l first:border-l-0 border-border transition-colors ${
                divisionFilter === opt.v
                  ? "bg-primary text-white"
                  : "bg-card text-ink hover:bg-page"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Tabla ----------

type SortKey = "code" | "client" | "driver" | "amount" | "status" | "date";
type SortDir = "asc" | "desc";

function OrdersTable({
  orders,
  clientNameById,
  assignmentByOrderId,
  selectedOrderId,
  onSelect,
}: {
  orders: Order[];
  clientNameById: Map<string, string>;
  assignmentByOrderId: Map<string, DriverAssignment>;
  selectedOrderId: string | null;
  onSelect: (id: string) => void;
}) {
  // Click en una columna → orden ascendente. Segundo click → descendente.
  // Tercer click sobre la misma columna → vuelve a orden default (sin sort).
  // Cambiar de columna empieza siempre en ascendente.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);
  const toggleSort = (key: SortKey) => {
    setSort((cur) => {
      if (!cur || cur.key !== key) return { key, dir: "asc" };
      if (cur.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const sorted = useMemo(() => {
    if (!sort) return orders;
    const dir = sort.dir === "asc" ? 1 : -1;
    const getDriverName = (o: Order) =>
      assignmentByOrderId.get(o.id)?.driverName ?? "";
    const getClientName = (o: Order) =>
      o.client_name ?? clientNameById.get(o.client_id) ?? "";
    return [...orders].sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "code":
          cmp = a.code.localeCompare(b.code);
          break;
        case "client":
          cmp = getClientName(a).localeCompare(getClientName(b));
          break;
        case "driver":
          cmp = getDriverName(a).localeCompare(getDriverName(b));
          break;
        case "amount":
          cmp = a.total_amount - b.total_amount;
          break;
        case "status":
          cmp = (STATUS_LABEL[a.status] ?? a.status).localeCompare(
            STATUS_LABEL[b.status] ?? b.status,
          );
          break;
        case "date":
          cmp = a.planned_date.localeCompare(b.planned_date);
          break;
      }
      return cmp * dir;
    });
  }, [orders, sort, clientNameById, assignmentByOrderId]);

  if (sorted.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center text-sub text-sm">
        No hay órdenes que coincidan con los filtros.
      </div>
    );
  }
  return (
    <div className="bg-card border border-border rounded-lg shadow-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-page text-sub border-b border-border">
          <tr className="text-left">
            <SortableTh sortKey="code" sort={sort} onClick={toggleSort}>Código</SortableTh>
            <SortableTh sortKey="client" sort={sort} onClick={toggleSort}>Cliente</SortableTh>
            <th className="px-3 py-2 text-xs font-medium">División</th>
            <SortableTh sortKey="driver" sort={sort} onClick={toggleSort}>Chofer / Repartidor</SortableTh>
            <SortableTh sortKey="amount" sort={sort} onClick={toggleSort} className="text-right">Monto</SortableTh>
            <SortableTh sortKey="status" sort={sort} onClick={toggleSort}>Estado</SortableTh>
            <SortableTh sortKey="date" sort={sort} onClick={toggleSort}>Fecha</SortableTh>
          </tr>
        </thead>
        <tbody>
          {sorted.map((o) => {
            // Prioridad: 1) wire denormalizado (siempre disponible
            // ahora), 2) store local (legacy), 3) fallback con id
            // corto si la BD tiene huérfanos.
            const clientName =
              o.client_name ||
              clientNameById.get(o.client_id) ||
              `Cliente ${o.client_id.slice(0, 8)}`;
            const assignment = assignmentByOrderId.get(o.id);
            const selected = selectedOrderId === o.id;
            return (
              <tr
                key={o.id}
                onClick={() => onSelect(o.id)}
                className={`border-b border-border last:border-b-0 cursor-pointer transition-colors ${
                  selected ? "bg-primary/5" : "hover:bg-page/60"
                }`}
              >
                <td className="px-3 py-2 font-mono text-xs text-primary">
                  {o.code}
                </td>
                <td className="px-3 py-2 text-ink">{clientName}</td>
                <td className="px-3 py-2">
                  <DivisionBadge division={o.division} />
                </td>
                <td className="px-3 py-2 text-ink text-xs">
                  {assignment ? (
                    <div className="flex flex-col">
                      <span>{assignment.driverName}</span>
                      <span className="text-sub font-mono text-[10px]">
                        {assignment.sequence !== null ? `#${assignment.sequence + 1}` : "s/orden"} · {assignment.routeCode}
                      </span>
                    </div>
                  ) : (
                    // Sin asignar: en SALUD significa "esperando que un
                    // repartidor escanee el barcode"; en CONSUMO significa
                    // "todavía no se planificó la ruta".
                    <span className="text-sub">
                      {(o.division ?? "").toUpperCase() === "SALUD"
                        ? "Sin repartidor"
                        : "Sin asignar"}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono text-ink">
                  S/ {o.total_amount.toFixed(2)}
                </td>
                <td className="px-3 py-2">
                  <Badge variant={STATUS_BADGE[o.status]}>
                    {STATUS_LABEL[o.status]}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-ink text-xs">
                  {o.planned_date}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/// Header de columna clickable. Click cicla: sin orden → asc → desc →
/// sin orden. Muestra flecha discreta arriba/abajo cuando está activa
/// para que el usuario vea cuál ordena y en qué dirección.
function SortableTh({
  children,
  sortKey,
  sort,
  onClick,
  className,
}: {
  children: React.ReactNode;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir } | null;
  onClick: (k: SortKey) => void;
  className?: string;
}) {
  const active = sort?.key === sortKey;
  const arrow = !active ? "↕" : sort?.dir === "asc" ? "↑" : "↓";
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={`px-3 py-2 font-medium text-[11px] uppercase tracking-wide cursor-pointer select-none hover:text-ink transition-colors ${
        active ? "text-ink" : ""
      } ${className ?? ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <span className={`text-[10px] ${active ? "text-primary" : "text-sub/60"}`}>
          {arrow}
        </span>
      </span>
    </th>
  );
}

// (Th original sin sort eliminado — todas las columnas son sortables.)

// ---------- Drawer detalle ----------

function OrderDetailDrawer({
  orderId,
  assignment,
  clientNameFallback,
  onClose,
}: {
  orderId: string;
  assignment: DriverAssignment | null;
  clientNameFallback: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(true);

  type SalesDoc = {
    id: string;
    doc_type: string;
    series: string;
    number: string;
    motivo?: string;
    importe: number;
    estado?: string;
    motivo_devolucion?: string;
    delivered_at?: string;
    returned_at?: string;
  };
  const [docs, setDocs] = useState<SalesDoc[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  // Evidencias: fotos y firma que el chofer capturó al cerrar la entrega.
  // Si la orden todavía no fue visitada, el backend devuelve attachments=[]
  // sin error — no rompemos el render.
  type Evidence = {
    has_delivery: boolean;
    has_exception: boolean;
    signed_by?: string;
    delivered_at?: string;
    notes?: string;
    reason_label?: string;
    attachments: Array<{ id: string; kind: string; url: string }>;
  };
  const [evidence, setEvidence] = useState<Evidence | null>(null);
  const [loadingEvidence, setLoadingEvidence] = useState(true);

  // Historial de POD (incluye deshechos) — para que el supervisor vea
  // cada acción del chofer/repartidor. Si está vacío la sección
  // simplemente no se renderiza.
  const [history, setHistory] = useState<PODHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingDetail(true);
    setLoadingDocs(true);
    setLoadingEvidence(true);
    setLoadingHistory(true);
    setDetail(null);
    setDocs([]);
    setEvidence(null);
    setHistory([]);

    void api.getOrder(orderId).then(
      (d) => {
        if (!cancelled) {
          setDetail(d);
          setLoadingDetail(false);
        }
      },
      () => {
        if (!cancelled) setLoadingDetail(false);
      },
    );

    void fetch(`/api/orders/${orderId}/sales-documents`)
      .then((r) => r.json() as Promise<{ data?: SalesDoc[] }>)
      .then((j) => {
        if (!cancelled) {
          setDocs(j.data ?? []);
          setLoadingDocs(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadingDocs(false);
      });

    void fetch(`/api/orders/${orderId}/evidence`)
      .then((r) => r.json() as Promise<Evidence>)
      .then((j) => {
        if (!cancelled) {
          setEvidence(j);
          setLoadingEvidence(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadingEvidence(false);
      });

    void api
      .getOrderPODHistory(orderId)
      .then((h) => {
        if (!cancelled) {
          setHistory(h);
          setLoadingHistory(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadingHistory(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const totalDocs = docs.reduce((s, d) => s + d.importe, 0);

  return (
    <aside className="w-[420px] bg-card border-l border-border flex flex-col overflow-hidden">
      <header className="px-4 py-3 border-b border-border flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-sub">
            Detalle de orden
          </div>
          <div className="font-mono text-sm text-primary truncate">
            {detail?.code ?? "Cargando…"}
          </div>
        </div>
        <button onClick={onClose} className="text-sub hover:text-ink p-1" title="Cerrar">
          ✕
        </button>
      </header>

      <div className="flex-1 overflow-auto">
        {/* HERO: monto grande + estado + cantidad de docs.
            El monto es la métrica primaria — número grande, mucho whitespace.
            Status badge funciona como tag secundario debajo. */}
        {detail && (
          <div className="px-4 py-4 border-b border-border bg-gradient-to-b from-page/30 to-transparent">
            <div className="text-[10px] uppercase tracking-wider text-sub mb-1">
              Total de la orden
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-sub text-sm">S/</span>
              <span className="text-3xl font-bold font-mono text-ink tabular-nums">
                {detail.total_amount.toLocaleString("es-PE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex items-center justify-between mt-2 gap-2">
              <Badge variant={STATUS_BADGE[detail.status]}>
                {STATUS_LABEL[detail.status]}
              </Badge>
              <div className="text-[11px] text-sub">
                {docs.length} documento{docs.length === 1 ? "" : "s"}
              </div>
            </div>
          </div>
        )}

        {/* Cliente — primera info crítica, sin colapso */}
        <Section title="Cliente" icon={<span>👤</span>}>
          {loadingDetail ? (
            <div className="text-sub text-xs">Cargando…</div>
          ) : (
            <>
              <div className="text-ink font-semibold text-base leading-tight">
                {detail?.client_name || clientNameFallback || "—"}
              </div>
              {detail?.client_address && (
                <div className="text-xs text-sub mt-1.5 flex items-start gap-1.5">
                  <span className="shrink-0">📍</span>
                  <span>{detail.client_address}</span>
                </div>
              )}
              {detail?.delivery_address &&
                detail.delivery_address !== detail.client_address && (
                  <div className="text-xs text-sub mt-1 pl-5">
                    <span className="font-medium">Entrega:</span>{" "}
                    {detail.delivery_address}
                  </div>
                )}
              {detail?.client_phone && (
                <a
                  href={`tel:${detail.client_phone}`}
                  className="text-xs text-primary hover:underline mt-1.5 flex items-center gap-1.5 font-mono"
                >
                  <span>📞</span>
                  <span>{detail.client_phone}</span>
                </a>
              )}
            </>
          )}
        </Section>

        {/* Chofer asignado — segunda info crítica, sin colapso */}
        <Section title="Chofer asignado" icon={<span>🚚</span>}>
          {assignment ? (
            <>
              <div className="text-ink font-semibold text-sm">
                {assignment.driverName}
              </div>
              <div className="text-xs text-sub mt-1 flex items-center gap-2 flex-wrap">
                {assignment.driverCode && (
                  <span className="font-mono px-1.5 py-0.5 rounded bg-page text-ink border border-border">
                    {assignment.driverCode}
                  </span>
                )}
                <span>Ruta {assignment.routeCode}</span>
                <span>·</span>
                <span>
                  {assignment.sequence !== null
                    ? `Parada #${assignment.sequence + 1}`
                    : "Parada s/orden"}
                </span>
              </div>
            </>
          ) : (
            <div className="text-sub text-sm italic">Sin chofer asignado</div>
          )}
        </Section>

        {/* Fecha de ruta — info contextual, colapsable */}
        {detail && (
          <Section
            title="Ruta y planificación"
            icon={<span>🗓</span>}
            collapsible
            defaultCollapsed
          >
            <div className="text-ink text-sm capitalize">
              {formatDateHuman(detail.planned_date)}
            </div>
            {detail.service_minutes > 0 && (
              <div className="text-xs text-sub mt-0.5">
                Tiempo de servicio estimado: {detail.service_minutes} min
              </div>
            )}
          </Section>
        )}

        {/* Boletas / facturas — colapsable, default cerrada. Para una
            orden con muchos docs el drawer se hacía muy largo; ahora
            el usuario abre cuando quiere ver el desglose. */}
        {/* Productos (DIVISION SALUD). Solo aparece cuando el backend
            devolvió items. CONSUMO se queda con el listado de docs. */}
        {detail?.items && detail.items.length > 0 && (
          <Section
            title="Productos"
            icon={<span>📦</span>}
            collapsible
            defaultCollapsed={false}
            trailing={
              <span className="text-[10px] text-sub">
                {detail.items.length} ítem{detail.items.length === 1 ? "" : "s"}
              </span>
            }
          >
            <div className="space-y-1.5">
              {detail.items.map((it) => (
                <div
                  key={it.id}
                  className="flex items-start justify-between gap-2 py-1 border-b border-border last:border-0"
                >
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <span className="inline-block shrink-0 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-mono font-bold">
                      {fmtQty(it.quantity)}×
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm text-ink leading-tight">
                        {it.description}
                      </div>
                      {it.sku && (
                        <div className="text-[10px] text-sub font-mono mt-0.5">
                          {it.sku}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs font-mono text-ink shrink-0">
                    S/ {it.line_total.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section
          title="Documentos"
          icon={<span>📃</span>}
          collapsible
          defaultCollapsed
          trailing={
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-sub">
                {docs.length} doc{docs.length === 1 ? "" : "s"}
              </span>
              {docs.length > 0 && (
                <span className="text-xs font-mono text-ink font-semibold">
                  S/ {totalDocs.toFixed(2)}
                </span>
              )}
            </div>
          }
        >
          {loadingDocs ? (
            <div className="text-sub text-xs py-2">Cargando…</div>
          ) : docs.length === 0 ? (
            <div className="text-sub text-xs py-2">Sin documentos vinculados</div>
          ) : (
            <div className="space-y-2">
              {docs.map((doc) => {
                const isReturn = (doc.motivo ?? "").toUpperCase() === "DEVOLUCION";
                const closed =
                  (doc.delivered_at && doc.delivered_at.length > 0) ||
                  (doc.returned_at && doc.returned_at.length > 0);
                return (
                  <div
                    key={doc.id}
                    className="flex items-start justify-between py-1.5 border-b border-border last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] text-sub font-semibold uppercase">
                          {doc.doc_type}
                        </span>
                        <span className="text-xs font-mono text-ink">
                          {doc.series}-{doc.number}
                        </span>
                        {isReturn && (
                          <span className="text-[9px] px-1 py-0.5 rounded bg-state-delayed/10 text-state-delayed border border-state-delayed/40">
                            DEV
                          </span>
                        )}
                      </div>
                      {doc.estado && (
                        <div
                          className="text-[10px] mt-0.5"
                          style={{ color: estadoColor(doc.estado) }}
                        >
                          {doc.estado}
                          {doc.motivo_devolucion && ` · ${doc.motivo_devolucion}`}
                        </div>
                      )}
                    </div>
                    <div
                      className={`text-sm font-mono font-semibold ${
                        closed && (doc.estado ?? "").toUpperCase().startsWith("DEV")
                          ? "line-through text-sub"
                          : "text-ink"
                      }`}
                    >
                      S/ {doc.importe.toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        <EvidenceSection
          loading={loadingEvidence}
          evidence={evidence}
        />

        <PODHistorySection loading={loadingHistory} entries={history} />

        {detail?.notes && (
          <Section title="Notas">
            <div className="text-sm text-ink whitespace-pre-wrap">{detail.notes}</div>
          </Section>
        )}

        {/* Mapa del negocio — solo el punto de entrega centrado, sin
            otras paradas. Va al final del drawer (debajo del detalle
            principal) para no robar atención de los datos críticos. */}
        {detail && detail.delivery_lat != null && detail.delivery_lon != null && (
          <Section title="Ubicación" icon={<span>📍</span>}>
            <LocationMiniMap
              lat={detail.delivery_lat}
              lon={detail.delivery_lon}
              subLabel={detail.delivery_address || detail.client_address}
              height={220}
            />
          </Section>
        )}
      </div>
    </aside>
  );
}

// ---------- Evidencias (fotos + firma del POD) ----------

type Evidence = {
  has_delivery: boolean;
  has_exception: boolean;
  signed_by?: string;
  delivered_at?: string;
  notes?: string;
  reason_label?: string;
  attachments: Array<{ id: string; kind: string; url: string }>;
};

/// PODHistorySection — registro de todos los POD (delivery + exception)
/// que el chofer/repartidor hizo para la orden, incluyendo los deshechos
/// (chip "DESHECHO"). Aporta auditoría al supervisor: ve cuándo se
/// registró, qué resultado, cuál fue deshecho y por quién.
function PODHistorySection({
  loading,
  entries,
}: {
  loading: boolean;
  entries: PODHistoryEntry[];
}) {
  // Si no hay entradas (orden todavía sin POD), no agregamos ruido.
  if (loading) {
    return (
      <Section title="Historial de registros" icon={<span>📝</span>}>
        <div className="text-sub text-xs py-1">Cargando…</div>
      </Section>
    );
  }
  if (entries.length === 0) {
    return null;
  }
  const fmt = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("es-PE", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };
  return (
    <Section
      title="Historial de registros"
      icon={<span>📝</span>}
      collapsible
      defaultCollapsed={entries.length > 3}
      trailing={
        <span className="text-[10px] text-sub">
          {entries.length} registro{entries.length === 1 ? "" : "s"}
        </span>
      }
    >
      <div className="space-y-2">
        {entries.map((e) => {
          const isDelivery = e.kind === "delivery";
          const accent = e.voided
            ? "border-sub/40 bg-page"
            : isDelivery
            ? "border-state-delivered/40 bg-state-delivered/5"
            : "border-state-failed/40 bg-state-failed/5";
          return (
            <div
              key={e.id}
              className={`border rounded p-2 text-xs ${accent} ${
                e.voided ? "opacity-70" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wide ${
                      isDelivery ? "text-state-delivered" : "text-state-failed"
                    }`}
                  >
                    {isDelivery ? "Entregado" : "Rechazado"}
                  </span>
                  {e.voided && (
                    <span className="px-1.5 py-0.5 rounded bg-sub/20 text-sub text-[9px] font-bold uppercase tracking-wide">
                      Deshecho
                    </span>
                  )}
                </div>
                <span className="text-[10px] text-sub font-mono">
                  {fmt(e.occurred_at)}
                </span>
              </div>
              <div className="text-ink">
                {e.actor_name || e.actor_user_id || "—"}
              </div>
              {!isDelivery && (e.reason_label || e.reason_code) && (
                <div className="text-[11px] text-sub mt-0.5">
                  Motivo: {e.reason_label || e.reason_code}
                </div>
              )}
              {isDelivery && e.delivered_total != null && (
                <div className="text-[11px] text-sub mt-0.5 font-mono">
                  S/ {e.delivered_total.toFixed(2)}
                </div>
              )}
              {e.notes && (
                <div className="text-[11px] text-sub mt-1 italic">
                  "{e.notes}"
                </div>
              )}
              {e.voided && (
                <div className="text-[10px] text-sub mt-1 border-t border-border/50 pt-1">
                  Deshecho {e.voided_at ? fmt(e.voided_at) : ""}
                  {e.voided_by_name ? ` por ${e.voided_by_name}` : ""}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function EvidenceSection({
  loading,
  evidence,
}: {
  loading: boolean;
  evidence: Evidence | null;
}) {
  const atts = evidence?.attachments ?? [];
  const photos = atts.filter((a) => a.kind === "photo");
  const signatures = atts.filter((a) => a.kind === "signature");
  const others = atts.filter((a) => a.kind !== "photo" && a.kind !== "signature");

  // Si no hubo entrega ni rechazo todavía, no renderizamos nada — el
  // chofer aún no visitó. Mostramos un placeholder muy discreto.
  if (!loading && evidence && !evidence.has_delivery && !evidence.has_exception) {
    return (
      <Section title="Evidencias" icon={<span>📸</span>}>
        <div className="text-xs text-sub italic">
          Sin evidencias todavía — el chofer aún no cerró esta visita.
        </div>
      </Section>
    );
  }

  return (
    <Section
      title="Evidencias"
      icon={<span>📸</span>}
      collapsible
      defaultCollapsed
      trailing={
        atts.length > 0 ? (
          <span className="text-[10px] text-sub">
            {photos.length > 0 && `${photos.length} foto${photos.length === 1 ? "" : "s"}`}
            {photos.length > 0 && signatures.length > 0 && " · "}
            {signatures.length > 0 && "firma"}
          </span>
        ) : null
      }
    >
      {loading ? (
        <div className="text-xs text-sub py-2">Cargando…</div>
      ) : (
        <div className="space-y-3">
          {/* Metadata del POD */}
          {evidence?.has_delivery && (
            <div className="text-xs text-sub space-y-0.5">
              {evidence.delivered_at && (
                <div>
                  <span className="font-medium text-ink">Entregado:</span>{" "}
                  {new Date(evidence.delivered_at).toLocaleString("es-PE", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </div>
              )}
              {evidence.signed_by && (
                <div>
                  <span className="font-medium text-ink">Recibido por:</span>{" "}
                  {evidence.signed_by}
                </div>
              )}
              {evidence.notes && (
                <div>
                  <span className="font-medium text-ink">Notas:</span> {evidence.notes}
                </div>
              )}
            </div>
          )}
          {evidence?.has_exception && evidence.reason_label && (
            <div className="text-xs">
              <span className="font-medium text-ink">Motivo de rechazo:</span>{" "}
              <span className="text-state-failed">{evidence.reason_label}</span>
              {evidence.notes && (
                <div className="text-sub mt-0.5">{evidence.notes}</div>
              )}
            </div>
          )}

          {/* Fotos: grid de miniaturas clickeables */}
          {photos.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-sub mb-1.5">
                Fotos ({photos.length})
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {photos.map((p) => (
                  <a
                    key={p.id}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block aspect-square rounded overflow-hidden border border-border hover:border-primary transition-colors"
                    title="Click para ver tamaño completo"
                  >
                    <img
                      src={p.url}
                      alt="Foto del POD"
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Firma: ancho completo (típicamente PNG con fondo blanco) */}
          {signatures.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-sub mb-1.5">
                Firma del cliente
              </div>
              {signatures.map((s) => (
                <a
                  key={s.id}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded border border-border bg-white overflow-hidden"
                >
                  <img
                    src={s.url}
                    alt="Firma del cliente"
                    loading="lazy"
                    className="w-full max-h-32 object-contain"
                  />
                </a>
              ))}
            </div>
          )}

          {/* Otros tipos de attachment (id_document, voucher, video) —
              poco frecuentes; los listamos como links genéricos. */}
          {others.length > 0 && (
            <div className="space-y-1">
              {others.map((o) => (
                <a
                  key={o.id}
                  href={o.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs text-primary hover:underline"
                >
                  {o.kind} →
                </a>
              ))}
            </div>
          )}

          {atts.length === 0 && evidence?.has_delivery && (
            <div className="text-xs text-sub italic">
              Entrega confirmada sin foto ni firma.
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

function Section({
  title,
  icon,
  children,
  trailing,
  collapsible = false,
  defaultCollapsed = false,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  trailing?: React.ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed);

  const header = (
    <div className="flex items-center justify-between mb-2">
      <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-sub font-medium">
        {icon}
        {title}
      </span>
      <div className="flex items-center gap-2">
        {trailing}
        {collapsible && (
          <span
            className={`text-[9px] text-sub transition-transform duration-150 ${
              collapsed ? "-rotate-90" : ""
            }`}
          >
            ▼
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="px-4 py-3 border-b border-border">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="w-full text-left -mx-1 px-1 -my-1 py-1 rounded hover:bg-page/40 transition-colors"
          aria-expanded={!collapsed}
        >
          {header}
        </button>
      ) : (
        header
      )}
      {!collapsed && children}
    </div>
  );
}

function estadoColor(estado: string): string {
  const e = estado.toUpperCase();
  if (e.includes("DEV") && e.includes("PARCIAL")) return STATUS_COLOR.partial;
  if (e.includes("DEV")) return STATUS_COLOR.failed;
  if (e.includes("DESPACHADO") || e.includes("ENTREGADO")) return STATUS_COLOR.delivered;
  return STATUS_COLOR.pending;
}

function daysAgoISO(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
